// src/pages/employer/profile/EmployerOverview.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { orgAPI } from '../../../services/api';
import api from '../../../services/api';
import {
  SectionModal, fieldModalInputStyle, fieldModalLabelStyle, RteModal,
} from '../../../components/shared/FieldModals';
import { EditBtn, AddBtn, DelBtn } from '../../../components/shared/UI';
import { Building2, Upload, CheckCircle, X, Gift, Trash2 } from 'lucide-react';

// ─── Shared styles ─────────────────────────────────────────

const inputStyle = fieldModalInputStyle;
const labelStyle = fieldModalLabelStyle;
const fw = { marginBottom: 14 };

// ─── Card field row (matches Workplaces pattern) ───────────

const fieldRowStyle  = { display: 'flex', alignItems: 'flex-start', padding: '11px 16px' };
const fieldLabelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  minWidth: 130, flexShrink: 0, paddingTop: 2, textAlign: 'right',
};
const fieldValueStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 14px', flex: 1 };
const emptyStyle      = { fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' };

const FieldEditBtn = ({ onClick, isAdmin }) => isAdmin ? (
  <EditBtn onClick={onClick} />
) : null;

const FieldRow = ({ label, value, onEdit, isAdmin }) => (
  <div style={fieldRowStyle}>
    <div style={fieldLabelStyle}>{label}:</div>
    <div style={fieldValueStyle}>
      {value != null && value !== ''
        ? <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>
        : <span style={emptyStyle}>Not set</span>
      }
      <FieldEditBtn onClick={onEdit} isAdmin={isAdmin} />
    </div>
  </div>
);

// ─── Section card ──────────────────────────────────────────

const Section = ({ title, children, onEdit, isAdmin, action }) => (
  <div className="section-box" style={{ marginBottom: 16 }}>
    <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{title}</span>
      {action || (onEdit && isAdmin && <EditBtn onClick={onEdit} title="Edit section" />)}
    </div>
    <div className="section-box-body">{children}</div>
  </div>
);

// ─── Logo uploader ─────────────────────────────────────────

const LogoUploader = ({ logoUrl, orgId, profileId, onUploaded }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file); form.append('type', 'LOGO'); form.append('name', file.name);
      const { data } = await api.post(`/orgs/${orgId}/profiles/${profileId}/media`, form);
      onUploaded(data.data.media.url);
    } catch {} finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
      <div onClick={() => fileRef.current.click()} style={{
        width: 72, height: 72, borderRadius: 12, border: '2px dashed var(--border)',
        background: 'var(--off-white)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', flexShrink: 0,
      }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--secondary)'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        {logoUrl
          ? <img src={logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <Upload size={20} style={{ color: 'var(--text-muted)' }} />
        }
      </div>
      <div>
        <button className="btn btn-outline btn-sm" onClick={() => fileRef.current.click()} disabled={uploading}>
          {uploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
        </button>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>PNG or SVG recommended. Max 5MB.</div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
};

// ─── Simple single-field modals ────────────────────────────

const TextFieldModal = ({ title, label, value, multiline, onSave, onCancel }) => {
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => { setSaving(true); try { await onSave(val.trim()); } finally { setSaving(false); } };
  return (
    <SectionModal title={title} onSave={handleSave} onCancel={onCancel} saving={saving}>
      <div style={fw}>
        <label style={labelStyle}>{label}</label>
        {multiline
          ? <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={6}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} autoFocus />
          : <input type="text" value={val} onChange={(e) => setVal(e.target.value)} style={inputStyle} autoFocus />
        }
      </div>
    </SectionModal>
  );
};

const NumberFieldModal = ({ title, label, value, onSave, onCancel }) => {
  const [val, setVal] = useState(value != null ? String(value) : '');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { await onSave(val !== '' ? parseInt(val, 10) : null); } finally { setSaving(false); }
  };
  return (
    <SectionModal title={title} onSave={handleSave} onCancel={onCancel} saving={saving}>
      <div style={fw}>
        <label style={labelStyle}>{label}</label>
        <input type="number" value={val} onChange={(e) => setVal(e.target.value)} style={inputStyle} autoFocus />
      </div>
    </SectionModal>
  );
};

const SelectFieldModal = ({ title, label, value, options, onSave, onCancel }) => {
  const [val, setVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => { setSaving(true); try { await onSave(val || null); } finally { setSaving(false); } };
  return (
    <SectionModal title={title} onSave={handleSave} onCancel={onCancel} saving={saving}>
      <div style={fw}>
        <label style={labelStyle}>{label}</label>
        <select value={val} onChange={(e) => setVal(e.target.value)} style={inputStyle} autoFocus>
          <option value="">— Select —</option>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </SectionModal>
  );
};

// ─── Social links modal (all links together per design rule) ─

const SocialLinksModal = ({ profile, onSave, onCancel }) => {
  const [form, setForm] = useState({
    website:      profile?.website      || '',
    linkedinUrl:  profile?.linkedinUrl  || '',
    twitterUrl:   profile?.twitterUrl   || '',
    facebookUrl:  profile?.facebookUrl  || '',
    instagramUrl: profile?.instagramUrl || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleSave = async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } };
  return (
    <SectionModal title="Edit Website & Social Links" onSave={handleSave} onCancel={onCancel} saving={saving}>
      {[['Website', 'website'], ['LinkedIn', 'linkedinUrl'], ['Twitter/X', 'twitterUrl'],
        ['Facebook', 'facebookUrl'], ['Instagram', 'instagramUrl']].map(([lbl, key]) => (
        <div key={key} style={fw}>
          <label style={labelStyle}>{lbl}</label>
          <input type="url" value={form[key]} onChange={set(key)} style={inputStyle} />
        </div>
      ))}
    </SectionModal>
  );
};

// ─── Company type / work style options ─────────────────────

const COMPANY_TYPE_OPTIONS = [
  ['startup', 'Startup'], ['sme', 'SME'], ['enterprise', 'Enterprise'],
  ['ngo', 'NGO / Non-profit'], ['govt', 'Government'], ['other', 'Other'],
];
const COMPANY_TYPE_LABEL = Object.fromEntries(COMPANY_TYPE_OPTIONS.map(([v, l]) => [v, l]));

const WORK_STYLE_OPTIONS = [['remote','Fully Remote'],['hybrid','Hybrid'],['onsite','On-site'],['flexible','Flexible']];
const WORK_STYLE_LABEL   = Object.fromEntries(WORK_STYLE_OPTIONS.map(([v, l]) => [v, l]));

// ─── Values editor (reused inside ValuesModal) ─────────────

const ValuesEditor = ({ values, onChange }) => {
  const [input, setInput] = useState('');
  const add = () => { const v = input.trim(); if (!v || values.includes(v)) return; onChange([...values, v]); setInput(''); };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {values.map((v, i) => (
          <div key={i} className="tag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {v}<Trash2 size={10} style={{ cursor: 'pointer', color: 'var(--text-muted)' }} onClick={() => remove(i)} />
          </div>
        ))}
        {values.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No values added yet</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={{ ...inputStyle, flex: 1 }} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="e.g. Integrity, Innovation…" maxLength={40} />
        <AddBtn onClick={add} size={13} />
      </div>
    </div>
  );
};

const ValuesModal = ({ profile, onSave, onCancel }) => {
  const [values, setValues] = useState(Array.isArray(profile?.values) ? profile.values : []);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => { setSaving(true); try { await onSave({ values }); } finally { setSaving(false); } };
  return (
    <SectionModal title="Edit Company Values" onSave={handleSave} onCancel={onCancel} saving={saving}>
      <ValuesEditor values={values} onChange={setValues} />
    </SectionModal>
  );
};

// ─── Awards editor ─────────────────────────────────────────

const AwardsEditor = ({ awards, onChange }) => {
  const add = () => onChange([...awards, { title: '', year: '', issuer: '' }]);
  const remove = (i) => onChange(awards.filter((_, idx) => idx !== i));
  const update = (i, k, v) => { const next = [...awards]; next[i] = { ...next[i], [k]: v }; onChange(next); };
  return (
    <div>
      {awards.map((a, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input style={inputStyle} placeholder="Award title" value={a.title} onChange={(e) => update(i, 'title', e.target.value)} />
          <input style={inputStyle} placeholder="Year" type="number" value={a.year} onChange={(e) => update(i, 'year', e.target.value)} />
          <input style={inputStyle} placeholder="Issuing organisation" value={a.issuer} onChange={(e) => update(i, 'issuer', e.target.value)} />
          <DelBtn onClick={() => remove(i)} />
        </div>
      ))}
      <AddBtn onClick={add} size={13} title="Add Award" />
    </div>
  );
};

const AwardsModal = ({ profile, onSave, onCancel }) => {
  const [awards, setAwards] = useState(Array.isArray(profile?.awards) ? profile.awards : []);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => { setSaving(true); try { await onSave({ awards }); } finally { setSaving(false); } };
  return (
    <SectionModal title="Edit Awards & Recognition" onSave={handleSave} onCancel={onCancel} saving={saving} wide>
      <AwardsEditor awards={awards} onChange={setAwards} />
    </SectionModal>
  );
};

// ─── Description tab ───────────────────────────────────────

const DescriptionTab = ({ profile, orgId, profileId, isAdmin, onSave }) => {
  const [modal, setModal] = useState(null);
  const close = () => setModal(null);

  const save = async (patch) => { await onSave(patch); close(); };

  const linkValue = (url) => url
    ? <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--secondary)' }}>{url}</a>
    : null;

  return (
    <>
      {/* ── Company Identity ── */}
      <Section title="Company Identity">
        {/* Logo */}
        <div style={{ padding: '12px 16px' }}>
          <LogoUploader logoUrl={profile?.logoUrl} orgId={orgId} profileId={profileId}
            onUploaded={(url) => onSave({ logoUrl: url })} />
        </div>

        <FieldRow label="Profile Name"  value={profile?.profileName}                                  onEdit={() => setModal('profileName')}  isAdmin={isAdmin} />
        <FieldRow label="Tagline"       value={profile?.tagline}                                      onEdit={() => setModal('tagline')}      isAdmin={isAdmin} />
        <FieldRow label="Company Type"  value={COMPANY_TYPE_LABEL[profile?.companyType] || null}      onEdit={() => setModal('companyType')}  isAdmin={isAdmin} />
        <FieldRow label="Industry"      value={profile?.industry}                                     onEdit={() => setModal('industry')}     isAdmin={isAdmin} />
        <FieldRow label="Founded Year"  value={profile?.foundedYear != null ? String(profile.foundedYear) : null} onEdit={() => setModal('foundedYear')} isAdmin={isAdmin} />
        <FieldRow label="ABN"           value={profile?.abn}                                          onEdit={() => setModal('abn')}          isAdmin={isAdmin} />
      </Section>

      {/* ── About ── */}
      <Section title="About" onEdit={() => setModal('about')} isAdmin={isAdmin}>
        <div style={{ padding: '12px 16px' }}>
          {profile?.about
            ? <div className="rte-editor" style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: profile.about }} />
            : <span style={emptyStyle}>Not set</span>
          }
        </div>
      </Section>

      {/* ── Website & Social Links ── */}
      <Section title="Website & Social Links" onEdit={() => setModal('social')} isAdmin={isAdmin}>
        {[['Website', 'website'], ['LinkedIn', 'linkedinUrl'], ['Twitter/X', 'twitterUrl'],
          ['Facebook', 'facebookUrl'], ['Instagram', 'instagramUrl']].map(([lbl, key]) => (
          <div key={key} style={fieldRowStyle}>
            <div style={fieldLabelStyle}>{lbl}:</div>
            <div style={fieldValueStyle}>
              {profile?.[key] ? linkValue(profile[key]) : <span style={emptyStyle}>Not set</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* ── Modals ── */}
      {modal === 'profileName' && (
        <TextFieldModal title="Edit Profile Name" label="Profile Name"
          value={profile?.profileName} onSave={(v) => save({ profileName: v })} onCancel={close} />
      )}
      {modal === 'tagline' && (
        <TextFieldModal title="Edit Tagline" label="Tagline"
          value={profile?.tagline} onSave={(v) => save({ tagline: v })} onCancel={close} />
      )}
      {modal === 'companyType' && (
        <SelectFieldModal title="Edit Company Type" label="Company Type"
          value={profile?.companyType} options={COMPANY_TYPE_OPTIONS}
          onSave={(v) => save({ companyType: v })} onCancel={close} />
      )}
      {modal === 'industry' && (
        <TextFieldModal title="Edit Industry" label="Industry"
          value={profile?.industry} onSave={(v) => save({ industry: v })} onCancel={close} />
      )}
      {modal === 'foundedYear' && (
        <NumberFieldModal title="Edit Founded Year" label="Founded Year"
          value={profile?.foundedYear} onSave={(v) => save({ foundedYear: v })} onCancel={close} />
      )}
      {modal === 'abn' && (
        <TextFieldModal title="Edit ABN" label="ABN"
          value={profile?.abn} onSave={(v) => save({ abn: v })} onCancel={close} />
      )}
      {modal === 'about' && (
        <RteModal title="Edit About"
          initialHtml={profile?.about || ''} onSave={(v) => save({ about: v })} onCancel={close} />
      )}
      {modal === 'social' && (
        <SocialLinksModal profile={profile} onSave={(patch) => save(patch)} onCancel={close} />
      )}
    </>
  );
};

// ─── Culture tab ───────────────────────────────────────────

const CultureTab = ({ profile, isAdmin, onSave }) => {
  const [modal, setModal] = useState(null);
  const close = () => setModal(null);
  const save = async (patch) => { await onSave(patch); close(); };

  return (
    <>
      {/* Mission & Work Style */}
      <Section title="Mission & Work Style">
        <FieldRow label="Work Style" value={WORK_STYLE_LABEL[profile?.workStyle] || null}
          onEdit={() => setModal('workStyle')} isAdmin={isAdmin} />
        <div style={fieldRowStyle}>
          <div style={fieldLabelStyle}>Mission:</div>
          <div style={{ ...fieldValueStyle, alignItems: 'flex-start' }}>
            {profile?.mission
              ? <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{profile.mission}</p>
              : <span style={emptyStyle}>Not set</span>
            }
            <EditBtn onClick={() => setModal('mission')} isAdmin={isAdmin} />
          </div>
        </div>
      </Section>

      {/* Company Values */}
      <Section title="Company Values" onEdit={() => setModal('values')} isAdmin={isAdmin}>
        {Array.isArray(profile?.values) && profile.values.length > 0
          ? <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {profile.values.map((v, i) => <span key={i} className="tag">{v}</span>)}
            </div>
          : <div style={{ padding: '10px 16px' }}><span style={emptyStyle}>No values added yet.</span></div>
        }
      </Section>

      {/* Diversity & Inclusion */}
      <Section title="Diversity & Inclusion" onEdit={() => setModal('di')} isAdmin={isAdmin}>
        <div style={{ padding: '12px 16px' }}>
          {profile?.diStatement
            ? <div className="rte-editor" style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7 }}
                dangerouslySetInnerHTML={{ __html: profile.diStatement }} />
            : <span style={emptyStyle}>Not set</span>
          }
        </div>
      </Section>

      {/* Awards & Recognition */}
      <Section title="Awards & Recognition" onEdit={() => setModal('awards')} isAdmin={isAdmin}>
        {Array.isArray(profile?.awards) && profile.awards.length > 0
          ? <div style={{ padding: '0 16px' }}>
              {profile.awards.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0',
                  borderBottom: i < profile.awards.length - 1 ? '1px solid var(--border)' : 'none',
                  alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.title}</div>
                    {a.issuer && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.issuer}</div>}
                  </div>
                  {a.year && <div style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{a.year}</div>}
                </div>
              ))}
            </div>
          : <div style={{ padding: '10px 16px' }}><span style={emptyStyle}>No awards added yet.</span></div>
        }
      </Section>

      {/* Modals */}
      {modal === 'workStyle' && (
        <SelectFieldModal title="Edit Work Style" label="Work Style"
          value={profile?.workStyle} options={WORK_STYLE_OPTIONS}
          onSave={(v) => save({ workStyle: v })} onCancel={close} />
      )}
      {modal === 'mission' && (
        <TextFieldModal title="Edit Mission Statement" label="Mission Statement"
          value={profile?.mission} multiline onSave={(v) => save({ mission: v })} onCancel={close} />
      )}
      {modal === 'di' && (
        <RteModal title="Edit Diversity & Inclusion"
          initialHtml={profile?.diStatement || ''} onSave={(v) => save({ diStatement: v })} onCancel={close} />
      )}
      {modal === 'values' && (
        <ValuesModal profile={profile} onSave={save} onCancel={close} />
      )}
      {modal === 'awards' && (
        <AwardsModal profile={profile} onSave={save} onCancel={close} />
      )}
    </>
  );
};

// ─── Benefits tab ──────────────────────────────────────────

const BENEFIT_CATEGORIES = [
  {
    key: 'health',
    label: 'Health & Wellbeing',
    presets: ['Private health insurance', 'Mental health support', 'Gym membership', 'EAP program', 'Dental cover', 'Vision cover'],
  },
  {
    key: 'financial',
    label: 'Financial',
    presets: ['Above-award salary', 'Performance bonuses', 'Superannuation above 11%', 'Share options/equity', 'Salary sacrifice', 'Financial planning support'],
  },
  {
    key: 'flexibility',
    label: 'Flexibility & Leave',
    presets: ['Work from home', 'Flexible hours', 'Extra annual leave', 'Paid parental leave', 'Purchased leave', 'Volunteer leave'],
  },
  {
    key: 'growth',
    label: 'Learning & Growth',
    presets: ['Training budget', 'Conference attendance', 'Mentoring program', 'Internal mobility', 'Study support', 'LinkedIn Learning'],
  },
  {
    key: 'perks',
    label: 'Perks & Lifestyle',
    presets: ['Free lunch / snacks', 'Team socials', 'Commuter benefits', 'Dog-friendly office', 'On-site parking', 'Discounts & rewards'],
  },
];

const parseBenefits = (raw) => (raw && typeof raw === 'object' ? raw : {});

const initCategoryState = (benefits) => {
  const state = {};
  BENEFIT_CATEGORIES.forEach(({ key }) => {
    state[key] = Array.isArray(benefits[key]) ? [...benefits[key]] : [];
  });
  return state;
};

const serializeBenefits = (state) => {
  const out = {};
  BENEFIT_CATEGORIES.forEach(({ key }) => { out[key] = state[key] || []; });
  return out;
};

// Category editor: plain list with delete + add (no checkboxes — selection happens in Job Ad Mockup)
const BenefitCategoryEditor = ({ category, items, onAdd, onRemove }) => {
  const [input, setInput] = useState('');
  const [dupError, setDupError] = useState(false);

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (items.some((item) => item.toLowerCase() === v.toLowerCase())) {
      setDupError(true);
      setTimeout(() => setDupError(false), 2000);
      return;
    }
    onAdd(v);
    setInput('');
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 8,
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {category.label}
      </div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
              padding: '3px 0', borderBottom: '1px solid var(--off-white)' }}>
              <span style={{ flex: 1 }}>{item}</span>
              <button type="button" onClick={() => onRemove(i)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '1px 3px',
                color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
              }}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inputStyle, flex: 1, fontSize: 12, borderColor: dupError ? '#CC0000' : undefined }}
            value={input}
            onChange={(e) => { setInput(e.target.value); if (dupError) setDupError(false); }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
            placeholder={`Add ${category.label.toLowerCase()} benefit…`} />
          <AddBtn onClick={add} size={13} />
        </div>
        {dupError && <div style={{ fontSize: 11, color: '#CC0000' }}>Already exists in this category.</div>}
      </div>
    </div>
  );
};

const BenefitsEditForm = ({ profile, onSave }) => {
  const [catState, setCatState] = useState(() => initCategoryState(parseBenefits(profile?.benefits)));

  const save = (next) => { onSave({ benefits: serializeBenefits(next) }); };

  const addItem = (catKey, val) => {
    const next = { ...catState, [catKey]: [...catState[catKey], val] };
    setCatState(next); save(next);
  };
  const removeItem = (catKey, idx) => {
    const next = { ...catState, [catKey]: catState[catKey].filter((_, i) => i !== idx) };
    setCatState(next); save(next);
  };

  return (
    <div>
      {BENEFIT_CATEGORIES.map((cat) => (
        <BenefitCategoryEditor key={cat.key} category={cat}
          items={catState[cat.key] || []}
          onAdd={(v) => addItem(cat.key, v)}
          onRemove={(i) => removeItem(cat.key, i)}
        />
      ))}
    </div>
  );
};

// Read-only view for non-admins
const BenefitCategory = ({ label, items }) => {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc' }}>
        {items.map((item, i) => (
          <li key={i} style={{ fontSize: 13, color: '#333', marginBottom: 2 }}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const BenefitsTab = ({ profile, isAdmin, onSave }) => {
  const benefits = parseBenefits(profile?.benefits);
  const hasAnyBenefit = BENEFIT_CATEGORIES.some(
    ({ key }) => Array.isArray(benefits[key]) && benefits[key].length > 0
  );

  if (isAdmin) {
    return (
      <Section title="Benefits & Perks">
        <BenefitsEditForm key={profile?.id} profile={profile} onSave={onSave} />
      </Section>
    );
  }

  return (
    <Section title="Benefits & Perks">
      {hasAnyBenefit
        ? BENEFIT_CATEGORIES.map(({ key, label }) => (
            <BenefitCategory key={key} label={label} items={benefits[key]} />
          ))
        : <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Gift size={36} style={{ color: 'var(--text-muted)', marginBottom: 10 }} />
            <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
              No benefits have been added yet.
            </p>
          </div>
      }
    </Section>
  );
};

// ─── Main page ─────────────────────────────────────────────

const TABS = [
  { key: 'description', label: 'Description' },
  { key: 'culture',     label: 'Culture'     },
  { key: 'benefits',   label: 'Benefits'    },
];

export const EmployerOverview = () => {
  const { activeOrg, activeProfile, refreshOrgs, isAdmin } = useAuth();
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    if (!activeOrg?.id || !activeProfile?.id) { setLoading(false); return; }
    orgAPI.getProfile(activeOrg.id, activeProfile.id)
      .then(({ data }) => setProfile(data.data.profile))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile]);

  // Save + refresh profile. Only refreshOrgs when identity fields change
  // (logo/name affect the sidebar). Culture fields don't need that refresh.
  const saveAndRefresh = async (patch, { refreshSidebar = false } = {}) => {
    await orgAPI.updateProfile(activeOrg.id, activeProfile.id, patch);
    const { data } = await orgAPI.getProfile(activeOrg.id, activeProfile.id);
    setProfile(data.data.profile);
    if (refreshSidebar) await refreshOrgs();
  };

  const descSave     = (patch) => saveAndRefresh(patch, { refreshSidebar: true });
  const cultureSave  = (patch) => saveAndRefresh(patch, { refreshSidebar: false });
  const benefitsSave = (patch) => saveAndRefresh(patch, { refreshSidebar: false });

  if (!activeOrg) return (
    <div className="fade-in">
      <div className="page-header"><h1>Overview</h1></div>
      <div className="section-box">
        <div className="section-box-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Building2 size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>Create an organisation to get started.</p>
          <button className="btn btn-primary btn-sm">Create Organisation</button>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="fade-in">
      <div className="page-header"><h1>Overview</h1></div>
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
    </div>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {profile?.logoUrl && (
            <img src={profile.logoUrl} alt="Logo" style={{ width: 48, height: 48, borderRadius: 10,
              objectFit: 'contain', border: '1px solid var(--border)', background: 'white', padding: 4 }} />
          )}
          <div>
            <h1>{profile?.profileName || 'Company Overview'}</h1>
            {profile?.tagline && <p style={{ marginTop: 2 }}>{profile.tagline}</p>}
          </div>
        </div>
        {profile?.isVerified && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
            <CheckCircle size={13} /> Verified
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid var(--border)',
        marginBottom: 20, background: 'white',
        borderRadius: '8px 8px 0 0', overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {TABS.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '12px 24px', fontSize: 13, fontWeight: active ? 700 : 500,
              color: active ? 'var(--secondary)' : 'var(--text-mid)',
              background: active ? '#EBF3FC' : 'transparent',
              border: 'none', borderBottom: active ? '2px solid var(--secondary)' : '2px solid transparent',
              cursor: 'pointer', transition: 'all 0.15s', marginBottom: -2, fontFamily: 'inherit',
            }}>
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'description' && (
        <DescriptionTab
          profile={profile} orgId={activeOrg.id} profileId={activeProfile.id}
          isAdmin={isAdmin} onSave={descSave}
        />
      )}
      {activeTab === 'culture' && (
        <CultureTab profile={profile} isAdmin={isAdmin} onSave={cultureSave} />
      )}
      {activeTab === 'benefits' && (
        <BenefitsTab profile={profile} isAdmin={isAdmin} onSave={benefitsSave} />
      )}
    </div>
  );
};
